import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import { ShoppingCentresTable } from "./components";
import { Pagination } from "@/components/common/Pagination";
import { PageHeader, PageHeaderWithBack, Button, useToast } from "@/components/common";
import { useAuth } from "@/context/AuthContext";
import { useAlert } from "@/hooks/useAlert";
import { shoppingCentresService } from "@/api/services/shoppingCentres.service";
import { AUSTRALIAN_STATES } from "@/data/australian-suburbs";
import styles from "./ShoppingCentres.module.css";
export const ShoppingCentres = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user } = useAuth();
    const basePath = location.pathname.startsWith("/staff") ? "/staff" : "/admin";
    const { error: showError } = useToast();
    const { confirm } = useAlert();
    const [searchParams, setSearchParams] = useSearchParams();
    const isMountedRef = useRef(true);
    const [centres, setCentres] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "");
    const [stateFilter, setStateFilter] = useState(searchParams.get("state") || "");
    const [showFilterMenu, setShowFilterMenu] = useState(false);
    const [isDeleting, setIsDeleting] = useState(null);
    const fetchCentres = useCallback(async () => {
        try {
            if (!isMountedRef.current)
                return;
            setLoading(true);
            setError(null);
            const filters = {
                search: searchTerm || undefined,
                state: stateFilter || undefined,
                page: currentPage,
                pageSize,
            };
            const response = await shoppingCentresService.getShoppingCentres(filters);
            if (!isMountedRef.current)
                return;
            const { data, pagination } = response.data;
            setCentres(data);
            setCurrentPage(pagination.pageNumber);
            setTotalPages(pagination.totalPages);
            setPageSize(pagination.pageSize);
        }
        catch (err) {
            if (!isMountedRef.current)
                return;
            setError("Failed to load shopping centres");
            console.error("Error fetching shopping centres:", err);
        }
        finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [searchTerm, stateFilter, currentPage, pageSize]);
    useEffect(() => {
        isMountedRef.current = true;
        fetchCentres();
        return () => {
            isMountedRef.current = false;
        };
    }, [fetchCentres]);
    const handleSearch = (e) => {
        if (e.key === "Enter") {
            const params = {};
            if (searchTerm)
                params.search = searchTerm;
            if (stateFilter)
                params.state = stateFilter;
            setSearchParams(params);
            setCurrentPage(1);
        }
    };
    const handleStateFilter = (state) => {
        setStateFilter(state);
        setCurrentPage(1);
        setShowFilterMenu(false);
        const params = {};
        if (searchTerm)
            params.search = searchTerm;
        if (state)
            params.state = state;
        setSearchParams(params);
    };
    const handleClearFilters = () => {
        setStateFilter("");
        setSearchParams({});
        setCurrentPage(1);
        setShowFilterMenu(false);
    };
    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: "smooth" });
    };
    const handleAddClick = () => {
        navigate(`${basePath}/shopping-centres/create`);
    };
    const handleEditClick = (id) => {
        navigate(`${basePath}/shopping-centres/${id}/edit`);
    };
    const handleDeleteClick = async (id) => {
        const confirmed = await confirm("Are you sure you want to delete this shopping centre?");
        if (!confirmed)
            return;
        try {
            setIsDeleting(id);
            await shoppingCentresService.deleteShoppingCentre(id);
            await fetchCentres();
        }
        catch (err) {
            console.error("Error deleting shopping centre:", err);
            showError("Failed to delete shopping centre");
        }
        finally {
            setIsDeleting(null);
        }
    };
    return (_jsxs("div", { className: styles.container, children: [_jsx(PageHeader, { userName: user?.name || "User", userEmail: user?.email || "" }), _jsx(PageHeaderWithBack, { title: "Shopping Centres", onBack: () => navigate(-1), actions: _jsxs("div", { className: styles.actions, children: [_jsxs("div", { className: styles.searchWrapper, children: [_jsx("span", { className: styles.searchIcon, children: _jsxs("svg", { width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [_jsx("circle", { cx: "11", cy: "11", r: "8" }), _jsx("path", { d: "m21 21-4.35-4.35" })] }) }), _jsx("input", { type: "text", className: styles.searchInput, placeholder: "Search", value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), onKeyPress: handleSearch, autoFocus: true })] }), _jsxs("div", { className: styles.filterDropdown, children: [_jsxs("button", { className: styles.filterBtn, onClick: () => setShowFilterMenu(!showFilterMenu), title: "Filter by state", children: [_jsx("svg", { width: "20", height: "20", viewBox: "0 0 24 24", fill: "currentColor", children: _jsx("path", { d: "M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" }) }), stateFilter && (_jsx("span", { className: styles.filterBadge, children: "1" }))] }), showFilterMenu && (_jsxs("div", { className: styles.filterMenu, children: [stateFilter && (_jsx("button", { onClick: handleClearFilters, className: styles.filterOption, children: "Clear Filters" })), AUSTRALIAN_STATES.map((s) => (_jsx("button", { onClick: () => handleStateFilter(s.code), className: `${styles.filterOption} ${stateFilter === s.code ? styles.activeFilter : ""}`, children: s.code }, s.code)))] }))] }), _jsx(Button, { className: styles.addButton, onClick: handleAddClick, variant: "secondary", children: "Add Shopping Centre" })] }) }), loading && (_jsx("div", { className: styles.loading, children: _jsx("p", { children: "Loading shopping centres..." }) })), error && (_jsxs("div", { className: styles.error, children: [_jsx("p", { children: error }), _jsx("button", { onClick: fetchCentres, className: styles.retryBtn, children: "Retry" })] })), !loading && !error && (_jsxs("div", { className: "table-page-listing", children: [_jsx(ShoppingCentresTable, { centres: centres, onEditClick: handleEditClick, onDeleteClick: handleDeleteClick, isDeleting: isDeleting }), _jsx(Pagination, { currentPage: currentPage, totalPages: totalPages, pageSize: pageSize, onPageChange: handlePageChange })] }))] }));
};
